import type { IconName } from '@fortawesome/fontawesome-common-types';
import { Card, CardContent, Stack, Typography } from '@mui/material';
import type { MouseEvent, MouseEventHandler, PropsWithChildren } from 'react';
import React from 'react';

import ContentLoader from '@components/ContentLoader';
import IconButton from '@components/IconButton';
import ProfileBubble from '@components/ProfileBubble';

interface ProfileCardProps {
  /** User or account name */
  name?: string | null;
  /** User or account profile picture */
  profile_picture?: string | null;
  fallback_profile_picture?: IconName;
  subtitle?: string | null;
  loading?: boolean;
  onClick?: MouseEventHandler;
  status?: React.ReactNode;
  disabled?: boolean;
}

const ProfileCard: React.FC<PropsWithChildren<ProfileCardProps>> = ({
  loading = false,
  children,
  fallback_profile_picture,
  name,
  profile_picture,
  status,
  subtitle,
  onClick,
  disabled = false,
}) => {
  const isClickable = Boolean(onClick);

  const handleClick = (event: MouseEvent) => {
    if (!isClickable) {
      return;
    }
    event.stopPropagation();
    onClick?.(event);
  };

  return (
    <Card
      onClick={handleClick}
      sx={{
        cursor: isClickable ? 'pointer' : 'default',
      }}
    >
      <CardContent sx={{ padding: 4 }}>
        <Stack spacing={2}>
          <Stack
            direction={'row'}
            justifyContent="flex-start"
            alignItems="center"
            spacing={0}
          >
            <ProfileBubble
              fallback={fallback_profile_picture ?? 'circle-user'}
              loading={loading}
              s3Key={profile_picture}
              size={44}
              style={{ opacity: disabled ? '50%' : undefined }}
            />
            <Stack
              direction="column"
              justifyContent="center"
              alignItems="flex-start"
              spacing={0.5}
              width="100%"
            >
              <ContentLoader loading={loading}>
                <Typography variant="body1">{name}</Typography>
                <Typography variant="caption">{subtitle}</Typography>
              </ContentLoader>
            </Stack>
            {status}
            {isClickable ? (
              <IconButton name="arrow-right" onClick={handleClick} />
            ) : null}
          </Stack>
          {children}
        </Stack>
      </CardContent>
    </Card>
  );
};

export default ProfileCard;
